"use client";

// THE VIEW-CHIP ROW'S TWO SMALL HABITS (law_one_chip_row, 2026-10-16).
//
// Every row of view chips is ONE row that never wraps: the navigator folds to its
// title and its chips (law_stage), and a head that wrapped to three lines on a
// phone would fold to three lines. A row that is wider than the screen scrolls
// sideways, and two things follow from that, both drawn from here for every row
// (NavChips for a panel's, ReorderableChips for a folder's):
//
//   · the edge says there is more that way. `data-edge` ("end" · "start" · "both")
//     is written straight onto the row (no state, no render), and the css fades the
//     row into it, so a chip is never cut in half (law_no_clipping) and a row that
//     fits carries no fade at all.
//   · the chip that is on stays in reach. A deep link to the last view, or a press
//     on a chip that was half under the fade, brings it fully into the row.
//
// The arithmetic is pure and tested (chipRow.test.ts); the hook is the DOM half.

import { useCallback, useRef } from "react";

export type ChipEdge = "start" | "end" | "both" | null;

/** which sides of the row have more behind them; null when the row fits */
export function chipEdgeOf(scrollLeft: number, scrollWidth: number, clientWidth: number): ChipEdge {
  const max = scrollWidth - clientWidth;
  if (max <= 1) return null;
  const start = scrollLeft > 1;
  const end = scrollLeft < max - 1;
  if (start && end) return "both";
  if (end) return "end";
  if (start) return "start";
  return null;
}

/** how far the row must scroll for the chip to stand clear of the fades (0 when it already does). Both are viewport rects. */
export function chipReveal(chip: { left: number; right: number }, row: { left: number; right: number }, pad: number): number {
  if (chip.right - chip.left > row.right - row.left - 2 * pad) return chip.left - row.left - pad; // wider than the row: begin at its start
  if (chip.left < row.left + pad) return chip.left - row.left - pad;
  if (chip.right > row.right - pad) return chip.right - row.right + pad;
  return 0;
}

/** a touch more than the fade at each end (`--chip-fade`, 1.6rem in the css), so the chip stands clear of it */
const REVEAL_PAD = 30;
const ON = '[data-on="true"], [aria-selected="true"], [aria-pressed="true"]';

/**
 * The row's ref callback: write `data-edge` as the row scrolls, resizes or changes
 * its chips, and keep the chip that is on in reach. Stable across renders (a new
 * callback each render would tear the observers down and scroll the row back under
 * a finger).
 */
export function useChipRow(): (el: HTMLElement | null) => void {
  const off = useRef<(() => void) | null>(null);
  return useCallback((el: HTMLElement | null) => {
    off.current?.();
    off.current = null;
    if (!el) return;

    const edge = () => {
      const e = chipEdgeOf(el.scrollLeft, el.scrollWidth, el.clientWidth);
      if (e) el.dataset.edge = e;
      else delete el.dataset.edge;
    };
    const reveal = (smooth: boolean) => {
      const on = el.querySelector<HTMLElement>(ON);
      if (!on) return;
      const by = chipReveal(on.getBoundingClientRect(), el.getBoundingClientRect(), REVEAL_PAD);
      if (!by) return;
      const calm = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
      el.scrollTo({ left: el.scrollLeft + by, behavior: smooth && !calm ? "smooth" : "auto" });
    };

    const raf = requestAnimationFrame(() => {
      edge();
      reveal(false);
    });
    el.addEventListener("scroll", edge, { passive: true });
    // a narrower row (a phone turned, a window narrowed) keeps the view that is on in reach
    const ro = typeof ResizeObserver === "function"
      ? new ResizeObserver(() => {
          edge();
          reveal(false);
        })
      : null;
    ro?.observe(el);
    // the chips change (a view appears, a count grows) and the one that is on changes
    const mo = typeof MutationObserver === "function"
      ? new MutationObserver((records) => {
          edge();
          if (records.some((r) => r.type === "attributes")) reveal(true);
        })
      : null;
    mo?.observe(el, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["data-on", "aria-selected", "aria-pressed"] });

    off.current = () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", edge);
      ro?.disconnect();
      mo?.disconnect();
      delete el.dataset.edge;
    };
  }, []);
}
